<template>
    <div
        :class="$options.name"
        class="flex flex-col md:flex-row py-6 px-4 space-y-5 md:space-y-0 md:space-x-6 border-b-2"
    >
        <div class="md:grow md:min-w-[31%]">
            <label for="search" class="block text-sm font-medium leading-5 text-gray-700">
                Terminsuche
            </label>
            <div class="flex mt-1 h-11 shadow-sm">
                <div class="flex relative focus-within:z-10 flex-grow items-stretch">
                    <div
                        class="flex absolute inset-y-0 left-0 items-center pl-3 pointer-events-none"
                    >
                        <CalendarIcon class="w-5 h-5 text-gray-500" aria-hidden="true" />
                    </div>
                    <input
                        id="search"
                        v-model="searchFilterValue"
                        type="search"
                        name="search"
                        :class="[computedUserFilterValues.search ? 'font-semibold' : '']"
                        class="block pl-10 w-full text-base leading-6 placeholder-gray-300 truncate border-gray-300 focus:border-primary focus:ring-primary"
                        placeholder="Veranstaltung suchen"
                    />
                </div>
            </div>
        </div>
        <Listbox
            v-if="!defaultFilter.hasDefaultEventCategoryFilter"
            v-model="eventFilterValue"
            as="div"
            class="md:grow md:min-w-[31%]"
        >
            <ListboxLabel class="block text-sm font-medium text-gray-700">
                Veranstaltung
            </ListboxLabel>
            <div class="relative mt-1">
                <ListboxButton
                    class="relative py-2 pr-10 pl-3 w-full h-11 text-base font-semibold leading-6 text-left bg-white border border-gray-300 focus:border-primary focus:ring-1 focus:ring-primary shadow-sm cursor-default focus:outline-none"
                >
                    <span
                        v-if="eventFilterValue"
                        class="block truncate border-gray-500"
                        v-html="eventFilterValue.label"
                    />
                    <span
                        class="flex absolute inset-y-0 right-0 items-center pr-2 pointer-events-none"
                    >
                        <SelectorIcon class="w-5 h-5 text-gray-400" aria-hidden="true" />
                    </span>
                </ListboxButton>

                <transition
                    leave-active-class="transition ease-in duration-100"
                    leave-from-class="opacity-100"
                    leave-to-class="opacity-0"
                >
                    <ListboxOptions
                        class="overflow-auto absolute z-10 py-1 mt-1 w-full max-h-60 text-base sm:text-sm bg-white ring-1 ring-black ring-opacity-5 shadow-lg focus:outline-none"
                    >
                        <ListboxOption
                            v-for="event in computedEventCategoryFilterValues"
                            :key="event.id"
                            v-slot="{ active, selected }"
                            as="template"
                            :value="event"
                        >
                            <li
                                :class="[
                                    active
                                        ? 'text-white bg-primary border-white'
                                        : 'text-gray-900 !border-gray-500',
                                    'cursor-default select-none relative py-2 px-4',
                                ]"
                            >
                                <span
                                    :class="[
                                        selected ? 'font-semibold' : 'font-normal',
                                        'block truncate',
                                    ]"
                                    v-html="event.label"
                                />
                                <span
                                    v-if="selected"
                                    :class="[
                                        active ? 'text-white' : 'text-primary',
                                        'absolute inset-y-0 left-0 flex items-center pl-1.5',
                                    ]"
                                >
                                    <CheckIcon class="w-5 h-5" aria-hidden="true" />
                                </span>
                            </li>
                        </ListboxOption>
                    </ListboxOptions>
                </transition>
            </div>
        </Listbox>
        <Listbox
            v-if="!defaultFilter.hasDefaultLocationCategoryFilter"
            v-model="locationFilterValue"
            as="div"
            class="md:grow md:min-w-[31%]"
        >
            <ListboxLabel class="block text-sm font-medium text-gray-700">
                Standortfilter
            </ListboxLabel>
            <div class="relative mt-1">
                <ListboxButton
                    class="relative py-2 pr-10 pl-3 w-full h-11 text-base font-semibold leading-6 text-left bg-white border border-gray-300 focus:border-primary focus:ring-1 focus:ring-primary shadow-sm cursor-default focus:outline-none"
                >
                    <span
                        v-if="locationFilterValue"
                        class="block truncate border-gray-500"
                        v-html="locationFilterValue.label"
                    />
                    <span
                        class="flex absolute inset-y-0 right-0 items-center pr-2 pointer-events-none"
                    >
                        <SelectorIcon class="w-5 h-5 text-gray-400" aria-hidden="true" />
                    </span>
                </ListboxButton>

                <transition
                    leave-active-class="transition ease-in duration-100"
                    leave-from-class="opacity-100"
                    leave-to-class="opacity-0"
                >
                    <ListboxOptions
                        class="overflow-auto absolute z-10 py-1 mt-1 w-full max-h-60 text-base sm:text-sm bg-white ring-1 ring-black ring-opacity-5 shadow-lg focus:outline-none"
                    >
                        <ListboxOption
                            v-for="location in computedLocationCategoryFiltersValues"
                            :key="location.id"
                            v-slot="{ active, selected }"
                            as="template"
                            :value="location"
                        >
                            <li
                                :class="[
                                    active
                                        ? 'text-white bg-primary border-white'
                                        : 'text-gray-900 !border-gray-500',
                                    'cursor-default select-none relative py-2 px-4',
                                ]"
                            >
                                <span
                                    :class="[
                                        selected ? 'font-semibold' : 'font-normal',
                                        'block truncate',
                                    ]"
                                    v-html="location.label"
                                />
                                <span
                                    v-if="selected"
                                    :class="[
                                        active ? 'text-white' : 'text-primary',
                                        'absolute inset-y-0 left-0 flex items-center pl-1.5',
                                    ]"
                                >
                                    <CheckIcon class="w-5 h-5" aria-hidden="true" />
                                </span>
                            </li>
                        </ListboxOption>
                    </ListboxOptions>
                </transition>
            </div>
        </Listbox>
    </div>
</template>

<script>
import { mapGetters, mapState } from 'vuex'
import { defineComponent } from 'vue'
import {
    Listbox,
    ListboxButton,
    ListboxLabel,
    ListboxOption,
    ListboxOptions,
} from '@headlessui/vue'
import { CalendarIcon } from '@heroicons/vue/outline'
import { CheckIcon, SelectorIcon } from '@heroicons/vue/solid'

export default defineComponent({
    name: 'UserFilter',
    components: {
        Listbox,
        ListboxButton,
        ListboxLabel,
        ListboxOption,
        ListboxOptions,
        CheckIcon,
        SelectorIcon,
        CalendarIcon,
    },
    data() {
        return {
            selectedType: {
                id: null,
                name: '<hr class="my-2.5 w-8 border rounded-full">',
            },
            selectedLocation: {
                id: null,
                name: '<hr class="my-2.5 w-8 border rounded-full">',
            },
        }
    },
    computed: {
        ...mapGetters({
            computedUserFilterValues: 'events/getUserFilter',
            computedEventCategories: 'events/getEventCategories',
            computedLocationCategories: 'events/getLocationCategories',
        }),
        ...mapState('events', { defaultFilter: 'defaultFilter' }),
        computedEventCategoryFilterValues() {
            const eventCategories = [
                {
                    id: null,
                    label: '<hr class="my-2.5 w-8 border rounded-full">',
                },
            ]
            eventCategories.push(...this.computedEventCategories)
            return eventCategories
        },
        computedLocationCategoryFiltersValues() {
            const eventLocations = [
                {
                    id: null,
                    label: '<hr class="my-2.5 w-8 border rounded-full">',
                },
            ]
            eventLocations.push(...this.computedLocationCategories)
            return eventLocations
        },
        searchFilterValue: {
            get() {
                return this.computedUserFilterValues.search
            },
            set(value) {
                this.$store.dispatch('events/updateSearchFilter', value)
            },
        },
        eventFilterValue: {
            get() {
                return this.computedUserFilterValues.event
            },
            set(value) {
                this.$store.dispatch('events/updateEventFilter', value)
            },
        },
        locationFilterValue: {
            get() {
                return this.computedUserFilterValues.location
            },
            set(value) {
                this.$store.dispatch('events/updateLocationFilter', value)
            },
        },
    },
})
</script>
